import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMediaPredicate } from 'react-media-hook';
import { useTranslation } from 'react-i18next';
import { User } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import Spinner from './Spinner';
import useAuthStore from 'store/auth';
import { PATH_ROUTES } from 'routes/path';

import Profile2 from 'assets/images/profiles/profile-2.png';
import Profile3 from 'assets/images/profiles/profile-3.png';
import Profile4 from 'assets/images/profiles/profile-4.png';
import Profile5 from 'assets/images/profiles/profile-5.png';
import useUpdateProfile from 'hooks/useUpdateProfile';
import { cn } from 'lib/utils';

const IMAGES = [Profile2, Profile3, Profile4, Profile5];

export default function FamilyMembers({
  onChange,
  className,
}: {
  onChange?: (id?: string) => void;
  className?: string;
}) {
  const { t } = useTranslation();

  const accountStatus = useAuthStore((store) => store.accountStatus);
  const currentMilestone = useAuthStore((store) => store.currentMilestone);
  const memberDetails = useAuthStore((store) => store.memberDetails);
  const profileId = useAuthStore((store) => store.currentUserProfileId);

  const { onUpdateUserAttributes } = useUpdateProfile();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const currentUser = memberDetails?.find(
    (member) => member.profile_id === profileId
  );
  const familyMembers = memberDetails
    .filter((member) => member.profile_id !== profileId)
    .sort((a, b) =>
      a.relation === 'Admin' ? -1 : b.relation === 'Admin' ? 1 : 0
    );

  const onProfileChange = async (profileId: string) => {
    setIsLoading(true);
    await onUpdateUserAttributes(profileId);
    onChange?.(profileId);
    setIsLoading(false);
  };

  const isMediaMobile = useMediaPredicate('(max-width: 640px)');

  if (['tnc', 'user-detail'].includes(currentMilestone)) {
    return null;
  }

  if (!accountStatus?.approved) return null;

  return (
    <>
      <Spinner isSpinning={isLoading} />

      <DropdownMenu>
        <DropdownMenuTrigger className='outline-none'>
          <div className='relative flex items-center cursor-pointer'>
            {familyMembers?.length ? (
              Array.from({
                length: isMediaMobile
                  ? 1
                  : familyMembers.length > 4
                  ? 4
                  : familyMembers.length,
              }).map((_, index) => (
                <div className='-ml-4' key={`profile-${index}`}>
                  <Avatar
                    className={cn('size-4 bg-rose-100 lg:size-8', className)}
                  >
                    <AvatarImage src={IMAGES[index]} alt='profile img' />
                    <AvatarFallback>U</AvatarFallback>
                  </Avatar>
                </div>
              ))
            ) : (
              <Avatar className={cn('size-3 bg-rose-100 lg:size-8', className)}>
                <AvatarFallback>
                  <User />
                </AvatarFallback>
              </Avatar>
            )}
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent className='w-56'>
          {familyMembers?.length ? (
            <>
              <DropdownMenuLabel className='text-center text-gray-600'>
                {t('select_different_user')}
              </DropdownMenuLabel>

              <DropdownMenuSeparator />

              <DropdownMenuGroup>
                <DropdownMenuLabel className='text-primary'>
                  {t('current_user')}
                </DropdownMenuLabel>
                <DropdownMenuItem className='pointer-events-none'>
                  {currentUser?.given_name} {currentUser?.family_name}
                </DropdownMenuItem>
              </DropdownMenuGroup>

              <DropdownMenuSeparator />

              {familyMembers?.length ? (
                <DropdownMenuGroup>
                  <DropdownMenuLabel className='text-primary'>
                    {t('other_members')}
                  </DropdownMenuLabel>
                  {familyMembers.map((member) => (
                    <DropdownMenuItem
                      key={member.profile_id}
                      onClick={() => onProfileChange(member.profile_id)}
                    >
                      <span>
                        {member.given_name} {member.family_name}
                      </span>
                      <span className='ml-2 text-secondary'>
                        {member.relation === 'Admin' ? `(${t('admin')})` : ''}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              ) : null}

              <DropdownMenuSeparator />
            </>
          ) : null}

          <div className='flex justify-center !my-2'>
            <DropdownMenuItem
              asChild
              className='rounded-full cursor-pointer bg-primary focus:bg-primary/80 focus:text-white'
            >
              <Link
                to={`${PATH_ROUTES.ADD_MEMBER}?redirectTo=${PATH_ROUTES.PROFILE}`}
                className='h-8 px-4 text-sm text-white'
              >
                {t('add_member')}
              </Link>
            </DropdownMenuItem>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
